import React from 'react';
import { Download, FileSpreadsheet } from 'lucide-react';
import type { ReportMode } from '../../../services/report.service';
import { useLanguage } from '../../../features/language/useLanguage';

interface GeneralReportHeaderProps {
  mode: ReportMode;
  onExportPdf: () => void;
  onExportExcel: () => void;
  isExportingPdf?: boolean;
  isExportingExcel?: boolean;
}

export const GeneralReportHeader: React.FC<GeneralReportHeaderProps> = ({
  mode,
  onExportPdf,
  onExportExcel,
  isExportingPdf = false,
  isExportingExcel = false,
}) => {
  const { t } = useLanguage();
  const reportMeta: Record<ReportMode, { title: string; description: string }> = {
    roasting: {
      title: t('reports.modes.roasting.label'),
      description: t('reports.modes.roasting.descriptionHeader'),
    },
    greenBeans: {
      title: t('reports.modes.greenBeans.label'),
      description: t('reports.modes.greenBeans.descriptionHeader'),
    },
    roastedBeans: {
      title: t('reports.modes.roastedBeans.label'),
      description: t('reports.modes.roastedBeans.descriptionHeader'),
    },
    finishedGoods: {
      title: t('reports.modes.finishedGoods.label'),
      description: t('reports.modes.finishedGoods.descriptionHeader'),
    },
    business: {
      title: t('reports.modes.business.label'),
      description: t('reports.modes.business.descriptionHeader'),
    },
    customer: {
      title: t('reports.modes.customer.label'),
      description: t('reports.modes.customer.descriptionHeader'),
    },
  };
  const meta = reportMeta[mode];

  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">{t('reports.header.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{meta.title}: {meta.description}</p>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto">
        <button
          type="button"
          onClick={onExportPdf}
          disabled={isExportingPdf}
          className="flex items-center justify-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 w-1/2 sm:w-auto"
          data-test-id="export-report-pdf-button"
        >
          <Download className="h-4 w-4 mr-2" />
          {isExportingPdf ? t('reports.header.exporting') : t('reports.header.exportPdf')}
        </button>
        <button
          type="button"
          onClick={onExportExcel}
          disabled={isExportingExcel}
          className="flex items-center justify-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 w-1/2 sm:w-auto"
          data-test-id="export-report-excel-button"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          {isExportingExcel ? t('reports.header.exporting') : t('reports.header.exportExcel')}
        </button>
      </div>
    </div>
  );
};
